/**
 * React Query Hooks for AI Scribe Recordings
 *
 * Query hooks use directApiClient for client-side fetching.
 * Mutation hooks use server actions for invalidation orchestration.
 *
 * Recordings are a top-level resource (LF-3713 / LF-3712 epic) — URL surface
 * is flat (`/recordings`) and most hooks no longer take a `matterId`. The
 * matter is implicit on the recording row. Only the hooks that genuinely
 * need a matter scope keep it on the signature:
 *  - `useRecordings(matterId, ...)` — the matter-tab list filter
 *  - `useCreateRecording(matterId)` — matter goes into the request body
 */

'use client';

import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient
} from '@tanstack/react-query';
import { toast } from '@/lib/toast';
import { directApiClient } from '@/lib/api/direct-client';
import {
  downloadRecordingDocx,
  downloadRecordingPdf,
  PdfServiceUnavailableError,
  type RecordingDocxScope,
  type RecordingPdfScope
} from './download';
import {
  recordingKeys,
  invalidateAfterRecordingUpdate
} from '@/lib/query-keys';
import { SMART_CACHE_CONFIG, FRESH_DATA_CONFIG } from '../query-config';
import type {
  RecordingListResponse,
  RecordingListParams,
  RecordingDetail,
  PreviewChangesResponse,
  CreateRecordingRequest,
  UpdateRecordingRequest,
  TranscriptSegment
} from './types';
import {
  createRecordingAction,
  getRecordingsListAction,
  updateRecordingAction,
  deleteRecordingAction,
  retryRecordingAction,
  reanalyseRecordingAction,
  regenerateRecordingSummaryAction
} from './actions';
import { regenerateHandoff } from './handoff-client';
import type { HandoffCreatedResponse } from './handoff-types';

// ─── Query Hooks ────────────────────────────────────────────────────

/**
 * Fetch paginated recordings list scoped to a matter (matter-tab view).
 */
export function useRecordings(
  matterId: number,
  page = 1,
  limit = 10,
  enabled = true
) {
  return useQuery({
    queryKey: recordingKeys.list(matterId, { page, limit }),
    queryFn: async () => {
      return directApiClient.get<RecordingListResponse>(
        `/recordings?matterId=${matterId}&page=${page}&limit=${limit}`
      );
    },
    enabled,
    ...SMART_CACHE_CONFIG
  });
}

/**
 * Page size shared by the firm-wide Scribe list and the lead-scoped read below
 * (the BE caps `limit` at 50).
 */
const SCRIBE_LIST_PAGE_SIZE = 50;

/**
 * A lead's own recordings — `GET /recordings?leadId=` (LF-5995's lead-scoped
 * capture).
 *
 * **This is the only read that resolves them.** `lead_meetings.recording_id`
 * exists as a column and a relation, but no shipped path writes it, so a lead's
 * detail read (`GET /leads/:id`) publishes nothing about a recording —
 * `LeadMeetingDto.recordingId` is `null` for every lead. The convert flow reaches
 * them the same way this does, by `recordings.lead_id`.
 *
 * `leadId === null` parks the read, so a caller can gate it on something else
 * (the rail's bind CTA only needs it once the lead has a linked matter).
 *
 * Unpaginated by design: a lead-scoped capture is find-or-create per lead, so
 * the realistic count is 0 or 1 and one page at the BE's own cap is the whole
 * set — no `hasNextPage` for callers to forget.
 */
export function useLeadRecordings(leadId: number | null, enabled = true) {
  return useQuery({
    queryKey: recordingKeys.leadList(leadId ?? 0),
    queryFn: async () => {
      return directApiClient.get<RecordingListResponse>('/recordings', {
        params: { leadId, limit: SCRIBE_LIST_PAGE_SIZE }
      });
    },
    enabled: leadId != null && enabled,
    ...SMART_CACHE_CONFIG
  });
}

// List poll cadence while a loaded row is uploading/processing — frequent
// enough that a finished recording flips to Ready promptly, gentle enough not
// to hammer the API from an idle list tab.
const SCRIBE_LIST_POLL_INTERVAL_MS = 7000;

/**
 * Firm-wide / matter-scoped recording list for the top-level Scribe surface
 * (LF-3712). Backs the home list, search, status filter, and the matter
 * recordings window (`?matterId=`). Paginated via `useInfiniteQuery` — pages
 * accumulate behind a "Load more" affordance.
 *
 * `status` is normalised to a sorted array so the cache key is stable
 * regardless of the order the lawyer toggled filters in.
 */
export function useScribeRecordings(
  params: Omit<RecordingListParams, 'page' | 'limit'> = {}
) {
  const normalisedStatus = params.status?.length
    ? [...params.status].sort()
    : undefined;

  return useInfiniteQuery({
    queryKey: recordingKeys.scribeList({
      q: params.q?.trim() || undefined,
      status: normalisedStatus,
      archived: params.archived || undefined,
      matterId: params.matterId
    }),
    queryFn: async ({ pageParam }) => {
      const result = await getRecordingsListAction({
        ...params,
        status: normalisedStatus as RecordingListParams['status'],
        page: pageParam,
        limit: SCRIBE_LIST_PAGE_SIZE
      });
      if (!result.success) throw new Error(result.error);
      return result.data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const currentPage = lastPage?.meta?.currentPage;
      const totalPages = lastPage?.meta?.totalPages;
      if (totalPages == null || currentPage == null) return undefined;
      return currentPage < totalPages ? currentPage + 1 : undefined;
    },
    ...SMART_CACHE_CONFIG,
    // The recordings list is a fast-changing collection touched from many
    // surfaces (create draft, record, process, archive, discard, bind). Always
    // fetch fresh when the list view loads rather than relying on every distant
    // mutation to invalidate — otherwise a recording created on /scribe/new
    // isn't visible on return until a hard refresh (SMART_CACHE_CONFIG defaults
    // to refetchOnMount:false + a 2-min staleTime).
    staleTime: 0,
    refetchOnMount: 'always',
    // Poll while any loaded row is still being worked on server-side
    // (uploading / processing), so the row flips to Ready without a manual
    // refresh. Stops as soon as nothing is in flight.
    refetchInterval: (query) => {
      const hasActive = (query.state.data?.pages ?? []).some((p) =>
        (p?.items ?? []).some(
          (r) => r.status === 'processing' || r.status === 'uploading'
        )
      );
      return hasActive ? SCRIBE_LIST_POLL_INTERVAL_MS : false;
    }
  });
}

/**
 * Fetch full recording detail (transcript, AI summary, etc.).
 * Supports polling via refetchInterval for processing status.
 */
export function useRecordingDetail(
  recordingId: number | null,
  options?: { refetchInterval?: number | false }
) {
  return useQuery({
    queryKey: recordingKeys.detail(recordingId ?? 0),
    queryFn: async () => {
      return directApiClient.get<RecordingDetail>(`/recordings/${recordingId}`);
    },
    enabled: !!recordingId,
    ...FRESH_DATA_CONFIG,
    refetchInterval: options?.refetchInterval ?? false
  });
}

/**
 * Fetch preview changes for import review (LF-3545).
 *
 * Live diff against current matter state — only valid for `ready` recordings.
 * The BE returns 409 for imported recordings; consumers reading the historical
 * post-import view should pull `appliedPreviewSnapshot` from the recording
 * detail instead and pass `enabled={false}` here.
 */
export function usePreviewChanges(recordingId: number | null, enabled = true) {
  return useQuery({
    queryKey: recordingKeys.preview(recordingId ?? 0),
    queryFn: async () => {
      return directApiClient.get<PreviewChangesResponse>(
        `/recordings/${recordingId}/preview-changes`
      );
    },
    enabled: !!recordingId && enabled,
    ...SMART_CACHE_CONFIG
  });
}

// ─── Mutation Hooks ─────────────────────────────────────────────────

/**
 * Create a new recording. The matter the recording belongs to lives in the
 * request body (every recording today still belongs to a matter; orphan
 * creation is a follow-up). The hook accepts `matterId` so the invalidation
 * payload can target the matter-scoped recording list.
 */
export function useCreateRecording(matterId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: CreateRecordingRequest) =>
      createRecordingAction(request),
    onSuccess: () => {
      invalidateAfterRecordingUpdate(queryClient, matterId);
    }
  });
}

/**
 * Update recording title or participants.
 */
export function useUpdateRecording(matterId: number, recordingId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: UpdateRecordingRequest) =>
      updateRecordingAction(recordingId, request),
    onSuccess: () => {
      invalidateAfterRecordingUpdate(queryClient, matterId, recordingId);
    }
  });
}

// ─── LF-3743: QR-handoff mutations ──────────────────────────────────

/**
 * Rotate the handoff token before any mobile has claimed it. BE returns
 * **409 `HANDOFF_ALREADY_CLAIMED`** if the row was claimed between the
 * desktop seeing `scan_expired` and clicking Regenerate — surface that to
 * the caller via the standard `onError` toast and re-fetch the recording
 * detail; the sheet flips to the `recording` phase pill on the next poll
 * because `handoffClaimedAt` is now set. No retry — Regenerate is one-shot.
 */
export function useRegenerateHandoff(recordingId: number, matterId?: number) {
  const queryClient = useQueryClient();

  return useMutation<HandoffCreatedResponse, Error, void>({
    mutationFn: () => regenerateHandoff(recordingId),
    onSuccess: () => {
      if (matterId != null) {
        invalidateAfterRecordingUpdate(queryClient, matterId, recordingId);
      } else {
        // Top-level draft (LF-3835): no matter scope. Minting/refreshing the
        // ambient QR doesn't change any list row, so bounce only the detail to
        // pull the fresh handoffPhase / handoffExpiresAt — leave the lists
        // alone (they'd refetch for nothing while a QR sits on screen).
        queryClient.invalidateQueries({
          queryKey: recordingKeys.detail(recordingId)
        });
      }
    }
  });
}

// ─── Top-level Scribe session mutations (matter-agnostic, LF-3712) ───
//
// The orphan session page (`/scribe/:id`) acts on a recording that may have no
// matter, so these hooks key only on `recordingId`. They invalidate the
// recording detail and every recording list (firm-wide `scribeList` + any
// matter-tab list) so the open page and the home list both refresh.

function invalidateScribeRecording(
  queryClient: ReturnType<typeof useQueryClient>,
  recordingId: number
) {
  queryClient.invalidateQueries({
    queryKey: recordingKeys.detail(recordingId)
  });
  queryClient.invalidateQueries({ queryKey: recordingKeys.lists() });
}

/**
 * Update a recording (title, summary text, transcript, extracted entities,
 * participants) without a matter scope. Editing `title` flips
 * `titleIsManual` server-side; transcript/summary edits require `ready`/
 * `imported` (the caller gates the affordance).
 */
export function useUpdateScribeRecording(recordingId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: UpdateRecordingRequest) =>
      updateRecordingAction(recordingId, request),
    onSuccess: () => invalidateScribeRecording(queryClient, recordingId)
  });
}

/**
 * Per-segment transcript edit (LF-3910). The lawyer edits or removes individual
 * transcript segments inline; each change re-sends the FULL edited `segments`
 * array plus a rebuilt `fullText` (the BE replaces the stored transcript
 * verbatim — D-31 — so we always send the whole thing). Optimistically patches
 * the detail cache so the line updates with no flicker, rolling back on error.
 *
 * `updateRecordingAction` resolves an `ActionResult` rather than throwing, so we
 * surface a failed save as a thrown error to drive React Query's rollback.
 */
export function useEditScribeTranscript(recordingId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (transcript: {
      fullText: string;
      segments: TranscriptSegment[];
    }) => {
      const result = await updateRecordingAction(recordingId, { transcript });
      if (!result.success) {
        throw new Error(result.error ?? 'Could not save the transcript.');
      }
      return result.data;
    },
    onMutate: async (transcript) => {
      const key = recordingKeys.detail(recordingId);
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<RecordingDetail>(key);
      if (previous) {
        queryClient.setQueryData<RecordingDetail>(key, {
          ...previous,
          transcript: {
            ...previous.transcript,
            fullText: transcript.fullText,
            segments: transcript.segments
          }
        });
      }
      return { previous };
    },
    onError: (_error, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(
          recordingKeys.detail(recordingId),
          context.previous
        );
      }
    },
    onSettled: () => invalidateScribeRecording(queryClient, recordingId)
  });
}

/**
 * Re-run LLM analysis (matter-agnostic). Status → processing → ready.
 *
 * `reanalyseRecordingAction` resolves an `ActionResult` rather than throwing, so
 * a BE rejection (e.g. a 409) comes back as `{ success: false }`. Surface that
 * as a thrown error so React Query fires `onError` (not `onSuccess`) and the
 * lawyer sees a toast — otherwise the confirm dialog closes with no visible
 * result and the recording silently stays Ready.
 */
export function useReanalyseScribeRecording(recordingId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const result = await reanalyseRecordingAction(recordingId);
      if (!result.success) {
        throw new Error(result.error ?? 'Could not reanalyse the recording.');
      }
      return result.data;
    },
    onSuccess: () => invalidateScribeRecording(queryClient, recordingId),
    onError: (error) =>
      toast.error("Couldn't reanalyse the recording", {
        description:
          error instanceof Error ? error.message : 'Please try again.'
      })
  });
}

/**
 * Re-generate the note against a different note template (VALE-480).
 *
 * Deliberately narrower than reanalyse: only `aiSummary` is rewritten, so the
 * lawyer's entity-card edits survive. Like reanalyse, the recording goes
 * `processing` → `ready` and the open page's poll carries it back, and like
 * reanalyse an `ActionResult` failure is re-thrown so React Query fires
 * `onError` and the lawyer sees a toast rather than a silently-unchanged note.
 */
export function useRegenerateRecordingSummary(recordingId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (templateId: string) => {
      const result = await regenerateRecordingSummaryAction(
        recordingId,
        templateId
      );
      if (!result.success) {
        throw new Error(result.error ?? 'Could not regenerate the note.');
      }
      return result.data;
    },
    onSuccess: () => invalidateScribeRecording(queryClient, recordingId),
    onError: (error) =>
      toast.error("Couldn't regenerate the note", {
        description:
          error instanceof Error ? error.message : 'Please try again.'
      })
  });
}

/** Retry a failed recording (matter-agnostic). */
export function useRetryScribeRecording(recordingId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => retryRecordingAction(recordingId),
    onSuccess: () => invalidateScribeRecording(queryClient, recordingId)
  });
}

/** Soft-delete a recording (matter-agnostic). */
export function useDeleteScribeRecording(recordingId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => deleteRecordingAction(recordingId),
    onSuccess: () => {
      // The recording no longer exists, so deliberately do NOT invalidate its
      // detail query: the session page's detail observer is still mounted at
      // this moment (navigation happens after the mutation resolves), and an
      // invalidation triggers an immediate refetch of the deleted id — a
      // guaranteed 404. Abort any in-flight detail fetch (e.g. a poll tick)
      // and refresh the lists so the row disappears; the stale detail cache
      // entry is unreachable once the caller navigates away and is GC'd.
      void queryClient.cancelQueries({
        queryKey: recordingKeys.detail(recordingId)
      });
      void queryClient.invalidateQueries({ queryKey: recordingKeys.lists() });
    }
  });
}

/**
 * Download the summary PDF for a recording at the chosen scope (FE-9).
 *
 * Streams a binary PDF via the proxy and triggers a browser download. A
 * `503 PDF_SERVICE_UNAVAILABLE` (Gotenberg down) surfaces as a retryable
 * toast; the caller keeps the flyout open so the lawyer can try again. No
 * cache invalidation — the export reads, it doesn't mutate.
 */
export function useDownloadRecordingPdf() {
  return useMutation({
    mutationFn: (args: {
      recordingId: number;
      scope: RecordingPdfScope;
      title?: string;
    }) => downloadRecordingPdf(args.recordingId, args.scope, args.title),
    onError: (error) => {
      if (error instanceof PdfServiceUnavailableError) {
        toast.error('PDF generation is temporarily unavailable', {
          description: 'Please try again in a moment.'
        });
      } else {
        toast.error("Couldn't download the PDF", {
          description: error instanceof Error ? error.message : undefined
        });
      }
    }
  });
}

/**
 * Download the note as a Word `.docx` at the chosen scope (VALE-480).
 *
 * Same transport and same retryable-503 handling as the PDF above; only the
 * scope set differs (no attachments — see `RecordingDocxScope`).
 */
export function useDownloadRecordingDocx() {
  return useMutation({
    mutationFn: (args: {
      recordingId: number;
      scope: RecordingDocxScope;
      title?: string;
    }) => downloadRecordingDocx(args.recordingId, args.scope, args.title),
    onError: (error) => {
      if (error instanceof PdfServiceUnavailableError) {
        toast.error('Word export is temporarily unavailable', {
          description: 'Please try again in a moment.'
        });
      } else {
        toast.error("Couldn't download the Word document", {
          description: error instanceof Error ? error.message : undefined
        });
      }
    }
  });
}
